import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';

import { NotificationHubService } from './core/services/notification-hub.service';
import { NotificationToastService } from './core/services/notification-toast.service';
import { UserService } from './core/services/user.service';
import { RecordsLandingService } from './core/services/records-landing.service';
import { NAV_GROUPS, NAV_GROUP_ORDER, NavGroup, NavItem } from './core/nav/nav-config';
import { BreadcrumbsComponent } from './shared/breadcrumbs/breadcrumbs.component';
import { NotificationBellComponent } from './core/components/notification-bell.component';

@Component({
  selector: 'nepf-root',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatToolbarModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    BreadcrumbsComponent,
    NotificationBellComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit {
  private readonly hub = inject(NotificationHubService);
  private readonly toast = inject(NotificationToastService);
  private readonly user = inject(UserService);
  private readonly recordsLanding = inject(RecordsLandingService);
  private readonly breakpoint = inject(BreakpointObserver);

  // Re-exposed to the template; computed signals re-fire when UserService.version() updates.
  readonly isAuthed = computed(() => this.user.isAuthenticated());
  readonly username = computed(() => this.user.username());
  readonly organizationName = computed(() => this.user.organizationName());

  // NAV-01/NAV-04: the sidenav renders from the single typed NAV_GROUPS config —
  // no scattered per-item canSee() gates in the template. The shell iterates
  // navGroups (ordered group headers); visibility is driven by each item's roles.
  readonly navGroups = NAV_GROUP_ORDER;

  /**
   * NAV-02 (#1 FIX): the role-resolved target for the single `/records` nav item.
   * Recomputes when roles change (UserService.profile signal feeds roles()).
   */
  readonly recordsRoute = computed(() =>
    this.recordsLanding.recordsRouteFor(this.user.roles()),
  );

  /**
   * Mobile / narrow-viewport state — drives sidenav mode (over vs side) and
   * default open state. The 1024px cutoff is just under our normal desktop
   * width; below that we treat the layout as constrained and overlay the
   * drawer instead of pushing main content sideways.
   */
  private readonly isNarrow = toSignal(
    this.breakpoint.observe('(max-width: 1024px)').pipe(map(r => r.matches)),
    { initialValue: false },
  );

  /** mat-sidenav.mode — 'over' on narrow viewports so it overlays main content. */
  readonly sidenavMode = computed<'side' | 'over'>(() =>
    this.isNarrow() ? 'over' : 'side',
  );

  /**
   * Sidenav open/close state. Backed by a signal (not a template #ref) because the toolbar
   * toggle button lives outside the @if block that renders the <mat-sidenav>; template-ref
   * variables don't escape @if scope in Angular 18+ control-flow blocks.
   */
  readonly drawerOpened = signal(true);

  constructor() {
    // Auto-collapse on narrow viewports; reopen when we cross back to desktop.
    effect(() => {
      this.drawerOpened.set(!this.isNarrow());
    });
  }

  /**
   * Plan 03-15 -- bootstrap the in-app push pipeline:
   *   1. Ensure the SignalR notifications hub is connected. NotificationHubService.connect() is
   *      idempotent (Plan 03-12), so calling it here is safe even though app.config.ts's
   *      APP_INITIALIZER chain already invokes it once at startup. This second call covers the
   *      Dev/Test path where the user logs in AFTER the initializer finished without an
   *      authenticated session.
   *   2. Start the toast subscription. NotificationToastService.start() is idempotent and listens
   *      on hub.push$ for every incoming notification, surfacing each as a MatSnackBar.
   *
   * Failures are logged and swallowed so a transient SignalR outage does NOT block the SPA from
   * rendering the rest of the application (the user can still upload + view submissions; they
   * just don't see real-time push toasts until reconnect succeeds).
   */
  async ngOnInit(): Promise<void> {
    // Token may have arrived after the initial Keycloak APP_INITIALIZER; refresh so the
    // username/roles signals re-evaluate against the now-authenticated session.
    this.user.refresh();
    // Best-effort /api/users/me bootstrap (roles + org name) for the toolbar;
    // errors are swallowed inside the service so a failed call just leaves the
    // org chip empty and nav minimal (ADR-0031).
    void this.user.ensureLoaded();
    try {
      await this.hub.connect();
      this.toast.start();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        'SignalR notifications hub failed to connect -- toasts will be silent until reconnect',
        err,
      );
    }
  }

  /**
   * NAV-01/NAV-04 template helper: the items in `group` visible to the current
   * user (role-gated by each item's `roles`). An empty result means the group
   * renders no header (no empty group shell).
   */
  visibleItems(group: NavGroup): NavItem[] {
    return NAV_GROUPS.filter(
      item => item.group === group && this.user.hasAnyRole(...item.roles),
    );
  }

  toggleDrawer(): void {
    this.drawerOpened.update(open => !open);
  }

  /**
   * WCAG 2.4.1 skip link. Handled in-component because the bare fragment
   * href resolves against <base href="/"> to path '/', which the router
   * treats as the role-landing redirect (yanks the user to /dashboard).
   * preventDefault stops that navigation; manual focus does the jump.
   */
  skipToMain(event: Event): void {
    event.preventDefault();
    const main = document.getElementById('main-content');
    main?.focus();
    main?.scrollIntoView();
  }

  /** Close the drawer after navigation when in overlay (mobile) mode. */
  onNavClick(): void {
    if (this.sidenavMode() === 'over') {
      this.drawerOpened.set(false);
    }
  }

  signOut(): void {
    void this.user.logout();
  }
}
